import { useMemo, useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridApi, GridOptions, ColGroupDef, ModuleRegistry } from 'ag-grid-community';
import { AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Installation } from '@shared/schema';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { InstallationDetail } from '@/features/operations/components/installations/installation-detail';
import { Construction, Eye, FileSpreadsheet, Upload, Download, RefreshCw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type Row = any;

// Helper function to convert date to input format - defined at module level for stability
function toInputDate(d: any): string {
  if (!d) return '';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return '';
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return '';
  }
}

// AG Grid custom date cell editor - functional component with forwardRef
// AG Grid v34 requires forwardRef + useImperativeHandle to expose getValue() to AG Grid
// IMPORTANT: Use a ref (not useState) for the value to ensure synchronous access in getValue()
const DateInputEditor = forwardRef((props: any, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const initialValue = props.value ? toInputDate(props.value) : '';
  // Use ref for synchronous value access - useState is async and causes timing issues with AG Grid
  const valueRef = useRef(initialValue);
  // Force re-render when value changes (for controlled input display)
  const [, forceUpdate] = useState(0);

  // Focus input on mount
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  // AG Grid calls getValue() when editing completes - ref ensures we always get current value
  useImperativeHandle(ref, () => ({
    getValue() {
      const val = valueRef.current;
      console.log('[DateInputEditor] getValue called, input value:', val);
      if (!val) return null;
      // Return ISO string with noon time to avoid timezone date shifts
      // "2024-12-04" → "2024-12-04T12:00:00" local time → ISO string
      // Using noon prevents the date from shifting to previous/next day in different timezones
      const date = new Date(val + 'T12:00:00');
      if (isNaN(date.getTime())) {
        console.warn('[DateInputEditor] Invalid date:', val);
        return null;
      }
      const isoString = date.toISOString();
      console.log('[DateInputEditor] Returning ISO string:', isoString);
      return isoString;
    },
    isCancelAfterEnd() {
      return false;
    },
    isCancelBeforeStart() {
      return false;
    },
    isPopup() {
      return false;
    },
    afterGuiAttached() {
      inputRef.current?.focus();
    },
  }));

  return (
    <input
      ref={inputRef}
      type="date"
      className="ag-input-field-input ag-text-field-input"
      style={{ width: '100%', height: '100%', padding: '0 6px' }}
      value={valueRef.current}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          props.api.stopEditing();
        }
        if (e.key === 'Escape') {
          props.api.stopEditing(true);
        }
      }}
      onBlur={() => {
        // Use timeout to allow onChange to fire first when selecting from calendar popup
        setTimeout(() => {
          props.api.stopEditing();
        }, 100);
      }}
      onChange={(e) => {
        const newValue = e.target.value || '';
        console.log('[DateInputEditor] onChange:', newValue);
        valueRef.current = newValue;

        // Convert to ISO string with noon time and update cell via setDataValue
        // Using noon (T12:00:00) prevents timezone shifts from changing the date
        if (props.node && props.colDef?.field) {
          let isoValue: string | null = null;
          if (newValue) {
            const date = new Date(newValue + 'T12:00:00');
            isoValue = isNaN(date.getTime()) ? null : date.toISOString();
          }
          console.log('[DateInputEditor] setDataValue:', isoValue);
          props.node.setDataValue(props.colDef.field, isoValue);
        }

        forceUpdate(n => n + 1);
      }}
    />
  );
});

DateInputEditor.displayName = 'DateInputEditor';

export default function InstallationGrid({ search }: { search?: string }) {
  const { toast } = useToast();
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const gridApi = useRef<GridApi | null>(null);
  const columnApi = useRef<any>(null);
  // Per-cell save state map: key = `${id}::${field}` value = 'saving'|'saved'|'error'
  const savingRef = useRef<Record<string, 'saving' | 'saved' | 'error'>>({});
  // Per-row pending updates with debounce timer
  const pendingRef = useRef<Record<string, { timer?: any; updates: Record<string, any> }>>({});

  const { data, isLoading, error } = useQuery<any[]>({
    queryKey: ['/api/installations/grid-view', { q: search || '' }],
    queryFn: async () => {
      const qs = new URLSearchParams({ limit: '800', ...(search ? { q: search } : {}) });
      const res = await fetch(`/api/installations/grid-view?${qs.toString()}`);
      if (!res.ok) throw new Error('Failed to load installations');
      return res.json();
    },
    staleTime: 15_000,
    gcTime: 60_000,
  });

  const rows: Row[] = useMemo(() => {
    const result = data || [];
    console.log('[InstallationGrid] Processing data:', {
      dataLength: result.length,
      firstRow: result[0],
      hasCustomer: (result[0] as any)?.customer,
      customerId: result[0]?.customerId
    });
    // Add a test row if no data to verify grid works
    if (result.length === 0 && !isLoading) {
      console.warn('[InstallationGrid] No data received, adding test row');
      return [{
        id: 1,
        installationAddress: 'TEST ADDRESS - No data from API',
        status: 'loa_mpan',
        customerId: 1
      }];
    }
    return result;
  }, [data, isLoading]);

  // Force grid refresh when data changes
  useEffect(() => {
    if (gridApi.current && rows.length > 0) {
      // In AG Grid v34, use setGridOption instead of setRowData
      gridApi.current.setGridOption('rowData', rows);
      setTimeout(() => {
        columnApi.current?.autoSizeAllColumns?.();
      }, 100);
    }
  }, [rows]);

  const onRowClicked = (event: any) => {
    const row: Row = event?.data;
    if (!row?.id) return;
    setSelectedId(Number(row.id));
    setOpenDetail(true);
  };

  const isDark = () => document.documentElement.classList.contains('dark');
  const [themeNonce, setThemeNonce] = useState(0);
  const themeClass = isDark() ? 'ag-theme-quartz-dark' : 'ag-theme-quartz';

  // React to theme changes (dark/light) and refresh grid visuals
  useEffect(() => {
    const observer = new MutationObserver(() => {
      gridApi.current?.refreshCells({ force: true });
      gridApi.current?.refreshHeader();
      setThemeNonce((n) => n + 1);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Sync global search into AG Grid quick filter for client-side narrowing
  useEffect(() => {
    const text = search || '';
    try {
      (gridApi.current as any)?.setGridOption?.('quickFilterText', text);
    } catch { }
    try {
      (gridApi.current as any)?.setQuickFilter?.(text);
    } catch { }
  }, [search]);

  // Status badge renderer - must return string or React component for AG Grid
  const statusCellRenderer = (p: any) => {
    const v = String(p.value || 'loa_mpan');
    const map: Record<string, string> = {
      loa_mpan: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      g98: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
      g99: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      site_survey: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      ready_to_schedule: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
      planning_for_installation: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
      prepared_to_install: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      waiting_dno_approval: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    };
    const labelMap: Record<string, string> = {
      loa_mpan: 'LoA & MPAN',
      g98: 'G98',
      g99: 'Waiting DNO (G99)',
      site_survey: 'Site Survey Done',
      ready_to_schedule: 'Ready to Schedule',
      planning_for_installation: 'Planning',
      prepared_to_install: 'Prepared',
      completed: 'Completed',
      waiting_dno_approval: 'Waiting DNO (G99)',
    };
    // Return a React component instead of DOM element
    return (
      <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${map[v] || ''}`}>
        {labelMap[v] || v.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
      </span>
    );
  };

  // Define column groups & basic columns – Phase 1-3
  const columnDefs = useMemo<(ColDef | ColGroupDef)[]>(
    () => [
      // Sticky/pinned left
      {
        headerName: 'ID',
        field: 'id',
        width: 80,
        pinned: 'left',
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Customer',
        field: 'customer.name',
        valueGetter: (p) => {
          // Try multiple ways to get customer name
          return p.data?.customer?.name ||
            p.data?.customerName ||
            `Customer #${p.data?.customerId || 'N/A'}`;
        },
        pinned: 'left',
        minWidth: 220,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Address',
        field: 'installationAddress',
        valueGetter: (p) =>
          p.data?.installationAddress ||
          p.data?.customer?.address ||
          p.data?.customerAddress ||
          p.data?.installation_address ||
          '—',
        pinned: 'left',
        minWidth: 260,
        flex: 1,
      },
      {
        headerName: 'Postcode',
        field: 'postcode',
        valueGetter: (p) =>
          p.data?.customer?.postcode || p.data?.customerPostcode || p.data?.postcode || '',
        width: 120,
      },

      // Group: Scheduling
      {
        headerName: 'Scheduling',
        children: [
          {
            headerName: 'Status',
            field: 'status',
            width: 160,
            sortable: true,
            cellRenderer: statusCellRenderer,
            editable: true,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: {
              values: ['loa_mpan', 'g98', 'g99', 'site_survey', 'ready_to_schedule', 'planning_for_installation', 'prepared_to_install', 'completed'],
            },
            cellStyle: (p) => indicatorStyle(p, 'status'),
          },
          {
            headerName: 'Install Date',
            field: 'installationDate',
            valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ''),
            width: 160,
            sortable: true,
            editable: true,
            cellEditor: DateInputEditor,
            cellStyle: (p) => mergeStyles(
              indicatorStyle(p, 'installationDate'),
              conditionalStyle(p, 'installationDate')
            ),
          },
          {
            headerName: 'Commission Date',
            field: 'commissioningDate',
            valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ''),
            width: 160,
            editable: true,
            cellEditor: DateInputEditor,
            cellStyle: (p) => mergeStyles(
              indicatorStyle(p, 'commissioningDate'),
              conditionalStyle(p, 'commissioningDate')
            ),
          },
        ],
      },

      // Group: Finance
      {
        headerName: 'Finance',
        children: [
          {
            headerName: 'Total Cost',
            field: 'totalCost',
            width: 140,
            valueFormatter: (p) => (p.value != null ? `£${Number(p.value).toLocaleString()}` : ''),
            editable: true,
            cellEditor: 'agTextCellEditor',
            valueParser: (p) => (p.newValue === '' ? null : Number(p.newValue)),
            cellStyle: (p) => indicatorStyle(p, 'totalCost'),
          },
          {
            headerName: 'Deposit',
            field: 'depositAmount',
            width: 120,
            editable: true,
            cellEditor: 'agTextCellEditor',
            cellStyle: (p) => indicatorStyle(p, 'depositAmount'),
          },
          {
            headerName: 'Final Payment Date',
            field: 'finalPaymentDate',
            valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ''),
            width: 170,
            editable: true,
            cellEditor: DateInputEditor,
            cellStyle: (p) => mergeStyles(
              indicatorStyle(p, 'finalPaymentDate'),
              conditionalStyle(p, 'finalPaymentDate')
            ),
          },
        ],
      },

      // Group: DNO
      {
        headerName: 'DNO',
        children: [
          { headerName: 'G98/G99', field: 'dnoApplicationType', width: 140, editable: true, cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['G98', 'G99'] }, cellStyle: (p) => indicatorStyle(p, 'dnoApplicationType') },
          { headerName: 'Provider', field: 'dnoProvider', width: 160, editable: true, cellEditor: 'agTextCellEditor', cellStyle: (p) => indicatorStyle(p, 'dnoProvider') },
          { headerName: 'App Sent', field: 'dnoApplicationSent', valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ''), width: 140, editable: true, cellEditor: DateInputEditor, cellStyle: (p) => indicatorStyle(p, 'dnoApplicationSent') },
          { headerName: 'Approved', field: 'dnoApproved', valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ''), width: 140, editable: true, cellEditor: DateInputEditor, cellStyle: (p) => mergeStyles(indicatorStyle(p, 'dnoApproved'), conditionalStyle(p, 'dnoApproved')) },
          { headerName: 'ENA Ref', field: 'dnoSystemReference', width: 180, editable: true, cellEditor: 'agTextCellEditor', cellStyle: (p) => indicatorStyle(p, 'dnoSystemReference') },
          { headerName: 'Case Ref', field: 'dnoCaseReference', width: 160, editable: true, cellEditor: 'agTextCellEditor', cellStyle: (p) => indicatorStyle(p, 'dnoCaseReference') },
          { headerName: 'Export Limit', field: 'dnoExportLimitation', width: 150, editable: true, cellEditor: 'agTextCellEditor', cellStyle: (p) => indicatorStyle(p, 'dnoExportLimitation') },
        ],
      },

      // Group: Serials & Numbers
      {
        headerName: 'Serials & Numbers',
        children: [
          { headerName: 'MPAN', field: 'mpanNumber', width: 180, editable: true, cellEditor: 'agTextCellEditor', cellStyle: (p) => indicatorStyle(p, 'mpanNumber') },
          { headerName: 'Export MPAN', field: 'exportMpan', width: 180, editable: true, cellEditor: 'agTextCellEditor', cellStyle: (p) => indicatorStyle(p, 'exportMpan') },
          { headerName: 'Inverter SN', field: 'inverterSerialNumber', width: 200, editable: true, cellEditor: 'agTextCellEditor', cellStyle: (p) => indicatorStyle(p, 'inverterSerialNumber') },
        ],
      },

      // Group: Checklists (priority items)
      {
        headerName: 'Checklists',
        children: [
          // HIES Registered + Date
          {
            headerName: 'HIES Registered',
            field: 'checklistHiesRegistered',
            width: 160,
            editable: true,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: ['pending', 'in_progress', 'completed', 'failed'] },
            cellStyle: (p) => indicatorStyle(p, 'checklistHiesRegistered'),
          },
          { headerName: 'HIES Reg. Date', field: 'hiesRegisteredDate', width: 150, editable: true, cellEditor: DateInputEditor, valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ''), cellStyle: (p) => indicatorStyle(p, 'hiesRegisteredDate') },

          // HIES Complete + Date
          {
            headerName: 'HIES Complete',
            field: 'checklistHiesReturnCompleted',
            width: 150,
            editable: true,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: ['pending', 'in_progress', 'completed', 'failed'] },
            cellStyle: (p) => indicatorStyle(p, 'checklistHiesReturnCompleted'),
          },
          { headerName: 'HIES Comp. Date', field: 'hiesCompletedDate', width: 150, editable: true, cellEditor: DateInputEditor, valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ''), cellStyle: (p) => indicatorStyle(p, 'hiesCompletedDate') },

          // DNO Commissioning Sent
          {
            headerName: 'DNO Commission Sent',
            field: 'checklistDnoCommissioningSent',
            width: 180,
            editable: true,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: ['pending', 'in_progress', 'completed', 'failed'] },
            cellStyle: (p) => indicatorStyle(p, 'checklistDnoCommissioningSent'),
          },

          // Final Payment Received
          {
            headerName: 'Final Payment Received',
            field: 'checklistFinalPaymentReceived',
            width: 200,
            editable: true,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: ['pending', 'in_progress', 'completed', 'failed'] },
            cellStyle: (p) => indicatorStyle(p, 'checklistFinalPaymentReceived'),
          },

          // Certificates
          { headerName: 'MCS Cert', field: 'checklistMcsCertificateProvided', width: 130, editable: true, cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['pending', 'in_progress', 'completed', 'failed'] }, cellStyle: (p) => indicatorStyle(p, 'checklistMcsCertificateProvided') },
          { headerName: 'EIC', field: 'checklistEicCertificateProvided', width: 110, editable: true, cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['pending', 'in_progress', 'completed', 'failed'] }, cellStyle: (p) => indicatorStyle(p, 'checklistEicCertificateProvided') },
          { headerName: 'Part P', field: 'checklistPartPBuildingRegsCertificate', width: 120, editable: true, cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['pending', 'in_progress', 'completed', 'failed'] }, cellStyle: (p) => indicatorStyle(p, 'checklistPartPBuildingRegsCertificate') },

          // Scaffolding
          { headerName: 'Scaffold PO', field: 'checklistPoForScaffold', width: 140, editable: true, cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['pending', 'in_progress', 'completed', 'failed'] }, cellStyle: (p) => indicatorStyle(p, 'checklistPoForScaffold') },
          { headerName: 'Strip Scaff Date', field: 'stripScaffDate', width: 160, editable: true, cellEditor: DateInputEditor, valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ''), cellStyle: (p) => indicatorStyle(p, 'stripScaffDate') },

          // EVCP Application
          { headerName: 'EVCP Application', field: 'checklistEvcpApplication', width: 170, editable: true, cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['pending', 'in_progress', 'completed', 'failed'] }, cellStyle: (p) => indicatorStyle(p, 'checklistEvcpApplication') },
        ],
      },

      // Signed
      {
        headerName: 'Signed',
        field: 'contractSigned',
        valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ''),
        width: 140,
        editable: (p) => !p?.data?.openSolarReference,
        cellEditor: DateInputEditor,
        cellStyle: (p) => mergeStyles(
          indicatorStyle(p, 'contractSigned'),
          conditionalStyle(p, 'contractSigned')
        ),
      },

      // Group: System Specification (NEW)
      {
        headerName: 'System Spec',
        children: [
          { headerName: 'Panel Model', field: 'panelModel', width: 150, editable: true, cellEditor: 'agTextCellEditor', cellStyle: (p) => indicatorStyle(p, 'panelModel') },
          { headerName: 'Battery Model', field: 'batteryModel', width: 150, editable: true, cellEditor: 'agTextCellEditor', cellStyle: (p) => indicatorStyle(p, 'batteryModel') },
          { headerName: 'Battery Qty', field: 'batteryQty', width: 100, editable: true, cellEditor: 'agTextCellEditor', valueParser: (p) => (p.newValue === '' ? null : Number(p.newValue)), cellStyle: (p) => indicatorStyle(p, 'batteryQty') },
        ],
      },

      // Group: Pre-Install Tracking (NEW)
      {
        headerName: 'Pre-Install',
        children: [
          { headerName: 'Folder Created', field: 'folderCreated', width: 130, editable: true, cellRenderer: (p: any) => p.value ? '✓' : '—', cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] }, cellStyle: (p) => indicatorStyle(p, 'folderCreated') },
          { headerName: 'Tradify Added', field: 'tradifyAdded', width: 130, editable: true, cellRenderer: (p: any) => p.value ? '✓' : '—', cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] }, cellStyle: (p) => indicatorStyle(p, 'tradifyAdded') },
          { headerName: 'Job Board', field: 'jobBoardAdded', width: 110, editable: true, cellRenderer: (p: any) => p.value ? '✓' : '—', cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] }, cellStyle: (p) => indicatorStyle(p, 'jobBoardAdded') },
          { headerName: 'Job Board Removed', field: 'jobBoardRemoved', width: 150, editable: true, cellRenderer: (p: any) => p.value ? '✓' : '—', cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] }, cellStyle: (p) => indicatorStyle(p, 'jobBoardRemoved') },
        ],
      },

      // Group: DNO Extended (NEW)
      {
        headerName: 'DNO Extended',
        children: [
          { headerName: 'Offer Letter', field: 'dnoOfferLetterReceived', width: 130, editable: true, cellEditor: DateInputEditor, valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ''), cellStyle: (p) => indicatorStyle(p, 'dnoOfferLetterReceived') },
          { headerName: 'Docusign', field: 'dnoDocusignStatus', width: 120, editable: true, cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['pending', 'sent', 'signed', ''] }, cellStyle: (p) => indicatorStyle(p, 'dnoDocusignStatus') },
          { headerName: 'Fee Paid', field: 'dnoFeePaid', width: 100, editable: true, cellRenderer: (p: any) => p.value ? '✓' : '—', cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] }, cellStyle: (p) => indicatorStyle(p, 'dnoFeePaid') },
          { headerName: 'Limit Added', field: 'dnoLimitAddedToSurvey', width: 120, editable: true, cellRenderer: (p: any) => p.value ? '✓' : '—', cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] }, cellStyle: (p) => indicatorStyle(p, 'dnoLimitAddedToSurvey') },
        ],
      },

      // Group: Scheduling Extended (NEW)
      {
        headerName: 'Scheduling Ext',
        children: [
          { headerName: 'Site Visit', field: 'siteVisitDate', width: 130, editable: true, cellEditor: DateInputEditor, valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ''), cellStyle: (p) => indicatorStyle(p, 'siteVisitDate') },
          { headerName: '7-Day Invoice', field: 'sevenDayInvoiceIssued', width: 120, editable: true, cellRenderer: (p: any) => p.value ? '✓' : '—', cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] }, cellStyle: (p) => indicatorStyle(p, 'sevenDayInvoiceIssued') },
          { headerName: 'RAMS', field: 'ramsCompleted', width: 90, editable: true, cellRenderer: (p: any) => p.value ? '✓' : '—', cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] }, cellStyle: (p) => indicatorStyle(p, 'ramsCompleted') },
        ],
      },

      // Group: On-Site Tracking (NEW)
      {
        headerName: 'On-Site',
        children: [
          { headerName: 'EVCP Response', field: 'evcpResponse', width: 130, editable: true, cellEditor: 'agTextCellEditor', cellStyle: (p) => indicatorStyle(p, 'evcpResponse') },
          { headerName: 'Sign-off', field: 'onsiteSignoffCompleted', width: 100, editable: true, cellRenderer: (p: any) => p.value ? '✓' : '—', cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] }, cellStyle: (p) => indicatorStyle(p, 'onsiteSignoffCompleted') },
          { headerName: 'Alterations', field: 'systemAlterations', width: 150, editable: true, cellEditor: 'agTextCellEditor', cellStyle: (p) => indicatorStyle(p, 'systemAlterations') },
          { headerName: 'Balance Invoice', field: 'balanceInvoiceIssued', width: 130, editable: true, cellRenderer: (p: any) => p.value ? '✓' : '—', cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] }, cellStyle: (p) => indicatorStyle(p, 'balanceInvoiceIssued') },
          { headerName: 'Next Steps Email', field: 'nextStepsEmailSent', width: 140, editable: true, cellRenderer: (p: any) => p.value ? '✓' : '—', cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] }, cellStyle: (p) => indicatorStyle(p, 'nextStepsEmailSent') },
          { headerName: 'EVCP Comm.', field: 'evcpCommissioningSent', width: 120, editable: true, cellRenderer: (p: any) => p.value ? '✓' : '—', cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] }, cellStyle: (p) => indicatorStyle(p, 'evcpCommissioningSent') },
          { headerName: 'Maint. Schedule', field: 'maintenanceScheduleProvided', width: 130, editable: true, cellRenderer: (p: any) => p.value ? '✓' : '—', cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] }, cellStyle: (p) => indicatorStyle(p, 'maintenanceScheduleProvided') },
        ],
      },

      // Actions pinned right
      {
        headerName: 'Actions',
        field: 'actions',
        pinned: 'right',
        width: 120,
        cellRenderer: (p: any) => {
          const id = p?.data?.id;
          if (!id) return null;
          return (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onRowClicked({ data: p.data });
              }}
            >
              <Eye className="h-3.5 w-3.5 mr-1" /> View
            </Button>
          );
        },
      },
    ],
    []
  );

  // Density toggle (row height)
  const [density, setDensity] = useState<'normal' | 'compact'>('normal');
  const rowHeight = density === 'compact' ? 35 : 45;

  // Column state persistence helpers
  const saveColumnState = () => {
    try {
      const state = columnApi.current?.getColumnState?.();
      if (state) localStorage.setItem('installation-grid-columns', JSON.stringify(state));
    } catch { }
  };
  const restoreColumnState = () => {
    try {
      const raw = localStorage.getItem('installation-grid-columns');
      if (!raw) return;
      const state = JSON.parse(raw);
      columnApi.current?.applyColumnState?.({ state, applyOrder: true });
    } catch { }
  };

  const gridOptions: GridOptions = useMemo(
    () => ({
      columnDefs,
      // Tell AG Grid to use the data's 'id' field as the row ID (required for getRowNode() to work)
      getRowId: (params) => String(params.data.id),
      // Phase 3: clipboard + undo/redo + range selection
      enableRangeSelection: true,
      enableFillHandle: true,
      undoRedoCellEditing: true,
      undoRedoCellEditingLimit: 50,
      suppressClipboardPaste: false,
      enableCellTextSelection: true,
      suppressRowClickSelection: false,
      rowSelection: 'multiple',
      animateRows: true,
      rowHeight,
      headerHeight: 40,
      singleClickEdit: true,
      stopEditingWhenCellsLoseFocus: true,
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        tooltipValueGetter: (p) => (p?.value != null ? String(p.value) : ''),
        editable: false,
      },
      onGridReady: (params) => {
        gridApi.current = params.api;
        columnApi.current = (params as any).columnApi;
        // Restore user column state if exists
        restoreColumnState();
        // Auto-size columns for content
        setTimeout(() => (params as any).columnApi?.autoSizeAllColumns?.(), 0);
      },
      onFirstDataRendered: (e) => {
        (e as any).columnApi?.autoSizeAllColumns?.();
      },
      onColumnMoved: saveColumnState,
      onColumnPinned: saveColumnState,
      onColumnVisible: saveColumnState,
      onColumnResized: (e) => {
        if (e.finished) saveColumnState();
      },
      onCellValueChanged: async (p) => {
        try {
          const row = p?.data;
          const id = Number(row?.id);
          const field = p?.colDef?.field as string;
          const newValue = p.newValue;
          const oldValue = p.oldValue;

          console.log('[InstallationGrid] onCellValueChanged:', {
            id,
            field,
            newValue,
            oldValue,
            newValueType: typeof newValue,
            oldValueType: typeof oldValue
          });

          if (!id || !field) {
            console.warn('[InstallationGrid] Missing id or field, skipping update');
            return;
          }

          // Skip if newValue is undefined - this indicates no real change
          if (newValue === undefined) {
            console.warn('[InstallationGrid] newValue is undefined, skipping update');
            return;
          }

          // Normalize values for comparison - handle Date/string/null equivalence
          const normalizeForCompare = (val: any): string | null => {
            if (val === '' || val === null || val === undefined) return null;
            // Convert Date objects to ISO string for consistent comparison
            if (val instanceof Date) {
              return isNaN(val.getTime()) ? null : val.toISOString();
            }
            // Convert date-like strings to ISO for comparison
            if (typeof val === 'string' && val.length > 0) {
              const parsed = Date.parse(val);
              if (!isNaN(parsed)) {
                return new Date(parsed).toISOString();
              }
            }
            return String(val);
          };

          const normalizedNew = normalizeForCompare(newValue);
          const normalizedOld = normalizeForCompare(oldValue);

          if (normalizedNew === normalizedOld) {
            console.log('[InstallationGrid] Values unchanged (after normalization), skipping update');
            return;
          }

          const cellKey = `${id}::${field}`;
          savingRef.current[cellKey] = 'saving';
          gridApi.current?.refreshCells({ rowNodes: [p.node], columns: [p.column.getId()], force: true });

          // Pass newValue - normalizeOutgoing handles Date/string conversion
          queueRowUpdate(id, { [field]: newValue }, p, cellKey);
        } catch (e) {
          console.error('onCellValueChanged error', e);
        }
      },
      // onRowClicked removed - users can click cells directly to edit, or use View button
    }),
    [columnDefs, rowHeight]
  );

  // Helper: compute cell style based on save state
  function indicatorStyle(p: any, field: string): any {
    try {
      const id = Number(p?.data?.id);
      const key = `${id}::${field}`;
      const state = savingRef.current[key];
      if (state === 'saving') return { backgroundColor: 'rgba(250, 204, 21, 0.15)' }; // amber-400/15
      if (state === 'saved') return { backgroundColor: 'rgba(34, 197, 94, 0.12)' }; // green-500/12
      if (state === 'error') return { backgroundColor: 'rgba(239, 68, 68, 0.12)' }; // red-500/12
      return {};
    } catch {
      return {};
    }
  }

  // Queue row update with debounce; consolidate multiple cell edits per row
  function queueRowUpdate(id: number, partial: Record<string, any>, p: any, cellKey: string) {
    const key = String(id);
    const existing = pendingRef.current[key] || { updates: {} };
    pendingRef.current[key] = existing;
    pendingRef.current[key].updates = { ...existing.updates, ...normalizeOutgoing(partial) };

    if (pendingRef.current[key].timer) clearTimeout(pendingRef.current[key].timer);
    pendingRef.current[key].timer = setTimeout(async () => {
      const payload = pendingRef.current[key]?.updates || {};
      delete pendingRef.current[key];
      await sendPatchImmediate(id, payload, partial);
    }, 550);
  }

  // Send patch immediately for a row payload and update cell indicators
  async function sendPatchImmediate(id: number, payload: Record<string, any>, originalPartial?: Record<string, any>) {
    try {
      console.log('[InstallationGrid] sendPatchImmediate called:', { id, payload });

      // Filter out undefined values (they would be stripped by JSON.stringify anyway)
      const cleanPayload: Record<string, any> = {};
      for (const [key, value] of Object.entries(payload)) {
        if (value !== undefined) {
          cleanPayload[key] = value;
        } else {
          console.warn(`[InstallationGrid] Filtering out undefined value for field: ${key}`);
        }
      }

      if (!cleanPayload || Object.keys(cleanPayload).length === 0) {
        console.warn('[InstallationGrid] Empty payload after cleanup, skipping PATCH');
        return;
      }

      // Always use the PATCH endpoint - it handles both single and multi-field updates
      console.log('[InstallationGrid] Sending PATCH to /api/installations/' + id, JSON.stringify(cleanPayload));
      const response = await apiRequest({ method: 'PATCH', url: `/api/installations/${id}`, data: cleanPayload });

      // Update AG Grid's row data with the response to prevent stale cache overwrites
      if (response && gridApi.current) {
        const rowNode = gridApi.current.getRowNode(String(id));
        if (rowNode) {
          // Merge response data into existing row data
          const updatedData = { ...rowNode.data, ...response };
          rowNode.setData(updatedData);
          console.log('[InstallationGrid] Updated row data with response:', { id, updatedFields: Object.keys(cleanPayload) });
        }
      }

      // Invalidate React Query cache to keep other views in sync
      queryClient.invalidateQueries({ queryKey: ['/api/installations/grid-view'] });
      queryClient.invalidateQueries({ queryKey: [`/api/installations/${id}`] });

      // Mark all affected cells as saved briefly
      Object.keys(cleanPayload).forEach((f) => {
        const k = `${id}::${f}`;
        savingRef.current[k] = 'saved';
      });
      gridApi.current?.refreshCells({ force: true });
      // Keep save indicator visible for 3 seconds for better user feedback
      setTimeout(() => {
        Object.keys(payload).forEach((f) => delete savingRef.current[`${id}::${f}`]);
        gridApi.current?.refreshCells({ force: true });
      }, 3000);
    } catch (err: any) {
      console.error('Patch failed', err);
      // Set error state on cells
      const fields = originalPartial ? Object.keys(originalPartial) : Object.keys(payload);
      fields.forEach((f) => {
        const k = `${id}::${f}`;
        savingRef.current[k] = 'error';
      });
      gridApi.current?.refreshCells({ force: true });
      // Optional: toast
      try {
        toast({ title: 'Save failed', description: err?.message || 'Unable to save changes', variant: 'destructive' });
      } catch { }
    }
  }

  // Normalize outgoing values for known field types
  function normalizeOutgoing(partial: Record<string, any>) {
    const out: Record<string, any> = {};
    const dateFields = new Set([
      'installationDate',
      'commissioningDate',
      'finalPaymentDate',
      'contractSigned',
      'dnoApplicationSent',
      'dnoApproved',
      'scheduledDate',
      'completedDate',
      'depositPaid',
      'sevenDayPaymentDate',
      'additionalPaymentDate',
      'hiesRegisteredDate',
      'hiesCompletedDate',
      'stripScaffDate',
      // New date fields from spreadsheet analysis
      'dnoOfferLetterReceived',
      'siteVisitDate',
    ]);
    const numericFields = new Set([
      'totalCost',
      'depositAmount',
      'sevenDayPaymentAmount',
      'finalPaymentAmount',
      'additionalPaymentAmount',
      'systemSize',
      'panelCount',
      // New numeric field from spreadsheet analysis
      'batteryQty',
    ]);
    for (const [k, v] of Object.entries(partial)) {
      // Skip undefined values entirely - they would be stripped by JSON.stringify anyway
      if (v === undefined) {
        console.warn(`[normalizeOutgoing] Skipping undefined value for field: ${k}`);
        continue;
      }
      // Convert empty string or null to null
      if (v === '' || v === null) {
        out[k] = null;
      }
      // Handle Date objects - convert to ISO string for consistent server parsing
      else if (dateFields.has(k) && v instanceof Date) {
        if (isNaN(v.getTime())) {
          console.warn(`[normalizeOutgoing] Invalid Date object for field: ${k}, setting to null`);
          out[k] = null;
        } else {
          out[k] = v.toISOString();
          console.log(`[normalizeOutgoing] Converted Date to ISO for ${k}:`, out[k]);
        }
      }
      // Handle date strings - keep as-is for server to parse
      else if (dateFields.has(k) && typeof v === 'string') {
        out[k] = v;
      }
      // Handle numeric fields - convert strings to numbers
      else if (numericFields.has(k) && typeof v === 'string') {
        out[k] = Number(v) || null;
      }
      // Keep all other values as-is
      else {
        out[k] = v;
      }
    }
    return out;
  }

  // Phase 1 enhancement: Flush pending with Cmd/Ctrl+S
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const sKey = e.key?.toLowerCase?.() || '';
      if ((e.metaKey || e.ctrlKey) && sKey === 's') {
        e.preventDefault();
        try {
          gridApi.current?.stopEditing?.();
        } catch { }
        // Flush all pending row updates immediately
        const entries = Object.entries(pendingRef.current);
        entries.forEach(([id, state]) => {
          try {
            if ((state as any).timer) clearTimeout((state as any).timer);
          } catch { }
          const payload = (state as any).updates || {};
          // Fire and forget; errors handled inside
          sendPatchImmediate(Number(id), payload);
          delete pendingRef.current[id];
        });
        toast({ title: 'Changes saved', description: 'All pending edits submitted.' });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Phase 3: Conditional formatting helper
  function conditionalStyle(p: any, field: string): any {
    try {
      const row = p?.data || {};
      const status = String(row?.status || '');
      const now = new Date();
      const dayMs = 24 * 60 * 60 * 1000;
      if (field === 'installationDate') {
        const v = row?.installationDate ? new Date(row.installationDate) : null;
        if (v && status !== 'completed') {
          if (v.getTime() < now.getTime() - dayMs) return { backgroundColor: 'rgba(239, 68, 68, 0.10)' }; // red/10 past due
          if (v.getTime() - now.getTime() < 3 * dayMs) return { backgroundColor: 'rgba(251, 191, 36, 0.12)' }; // amber/12 upcoming
        }
      }
      if (field === 'finalPaymentDate') {
        if (status === 'completed' && !row?.finalPaymentDate) {
          return { backgroundColor: 'rgba(250, 204, 21, 0.12)' }; // amber hint: payment missing
        }
      }
      if (field === 'dnoApproved') {
        if (status === 'waiting_dno' && !row?.dnoApproved) {
          return { backgroundColor: 'rgba(186, 230, 253, 0.15)' }; // sky/15 waiting for approval
        }
      }
      return {};
    } catch {
      return {};
    }
  }

  function mergeStyles(...styles: any[]): any {
    const out: any = {};
    for (const s of styles) {
      if (s && typeof s === 'object') Object.assign(out, s);
    }
    return out;
  }

  if (isLoading) {
    return (
      <Card className="card-modern fade-in">
        <CardHeader className="card-header">
          <CardTitle className="card-title flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <Construction className="h-4 w-4 text-slate-400" />
            </div>
            Loading Grid
          </CardTitle>
          <CardDescription className="card-description">Fetching installations…</CardDescription>
        </CardHeader>
        <CardContent className="card-content">
          <Skeleton className="h-[420px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="card-modern">
        <CardHeader>
          <CardTitle>Failed to load</CardTitle>
          <CardDescription>{(error as any)?.message || 'Unknown error'}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <Card className="card-modern">
        <CardHeader className="card-header">
          <CardTitle className="card-title">Installations Grid</CardTitle>
          <CardDescription className="card-description">
            Editable grid with inline editing. Click cells to edit, click View button for details.
          </CardDescription>
        </CardHeader>
        <CardContent className="card-content">
          <div className="flex items-center justify-between mb-3 gap-2">
            {/* Global search controls the data + quickFilter; local input hidden */}
            <div />
            <div className="text-xs text-muted-foreground">
              {rows.length} row{rows.length === 1 ? '' : 's'} | {isLoading ? 'Loading...' : data ? `Data loaded: ${data.length} items` : 'No data'}
            </div>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <Button
              variant={density === 'normal' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setDensity('normal');
                gridApi.current?.setGridOption?.('rowHeight', 45);
                gridApi.current?.resetRowHeights?.();
              }}
            >
              Normal
            </Button>
            <Button
              variant={density === 'compact' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setDensity('compact');
                gridApi.current?.setGridOption?.('rowHeight', 35);
                gridApi.current?.resetRowHeights?.();
              }}
            >
              Compact
            </Button>
            <div className="ml-auto" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => columnApi.current?.autoSizeAllColumns?.()}
            >
              Auto-size Columns
            </Button>
          </div>
          <div className={`${themeClass} w-full`} style={{ height: '68vh', minHeight: 520 }}>
            <AgGridReact
              rowData={rows}
              gridOptions={gridOptions}
              suppressDragLeaveHidesColumns
              onGridReady={(params) => {
                console.log('[InstallationGrid] Grid ready, setting data:', rows.length, 'rows');
                params.api.setGridOption('rowData', rows);
                gridOptions.onGridReady?.(params);
              }}
            />
          </div>
        </CardContent>
      </Card>

      <Sheet open={openDetail} onOpenChange={setOpenDetail}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-3xl md:max-w-4xl lg:max-w-5xl overflow-y-auto p-0"
        >
          <SheetHeader>
            <SheetTitle>Installation Details</SheetTitle>
          </SheetHeader>
          <div className="py-4 px-4">
            {selectedId && <InstallationDetail id={selectedId} />}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
